// App shell: session bootstrap, hash routing, sidebar/topbar chrome, language switch.
//
// Routing is hash-based on purpose — the SPA is served by the API's static handler with an
// index.html fallback, and a hash route needs no server cooperation to deep-link.
//
// The navigation a role sees is derived from `user.role`, but that is CONVENIENCE ONLY:
// every route is enforced again on the server (src/engine/access.js). Hiding a menu item
// is not a permission.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from './api';
import type { Lang, Meta, PanelUser } from './types';
import { Avatar, BrandFilter, Icon, LangSwitch } from './ui';
import { ROLE_KICKER, longDate } from './format';
import { useTranslateDOM } from './i18n';
import Login from './pages/Login';
import MyDashboard from './pages/MyDashboard';
import QmsDashboard from './pages/QmsDashboard';
import EvalDetail from './pages/EvalDetail';
import Evaluations from './pages/Evaluations';
import NewEvaluation from './pages/NewEvaluation';
import Objections from './pages/Objections';
import AgentReport from './pages/AgentReport';
import Reports from './pages/Reports';
import Users from './pages/Users';
import ChangePassword from './pages/ChangePassword';
import WeeklyReports, { WeeklyReportBanner } from './pages/WeeklyReports';
import Assignments from './pages/Assignments';

const APP_VERSION = 'v0.2';

export interface Route { name: string; param?: string; }

function parseHash(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const [name, param] = raw.split('/');
  return { name: name || 'home', param };
}

export function navigate(path: string) {
  window.location.hash = `#/${path.replace(/^\/+/, '')}`;
}

function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(parseHash);
  useEffect(() => {
    const on = () => setRoute(parseHash());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}

interface NavItem { key: string; label: string; icon: string; badge?: number; }

function navItemsFor(user: PanelUser, openObjections: number, openAssigned: number): NavItem[] {
  if (user.role === 'agent') {
    // İtirazlarım (owner 2026-09-21, Task Board #122/#123): the agent's own appeals, each with
    // its reply and outcome — the same list route, self-scoped by the server. The badge is the
    // count still awaiting an answer.
    return [
      { key: 'home', label: 'Panom', icon: 'dashboard' },
      { key: 'evaluations', label: 'Değerlendirmelerim', icon: 'fact_check' },
      { key: 'objections', label: 'İtirazlarım', icon: 'gavel', badge: openObjections || undefined },
      { key: 'report', label: 'Gelişimim', icon: 'insights' },
    ];
  }
  // A QMS specialist: evaluations + new evaluation (owner 2026-09-18) and, since 2026-09-22,
  // İtirazlar — read-only (owner, Task Board #122/#123 corrected: "qms agents should see
  // objections"). The appeals are against their own scoring, so they read the queue and the
  // thread; the server still refuses them resolve and every report route (403), so no other
  // entry is drawn. Badge = appeals still waiting on a supervisor's answer.
  // A qms with the read grant (viewAll, migration 022, owner 2026-10-15) reads like a supervisor —
  // Genel Bakış, every specialist's assignments, Raporlar — but still works as a specialist and
  // manages nobody: no Kullanıcılar, and the server keeps every action narrow.
  if (user.role === 'qms' && user.viewAll === true) {
    return [
      { key: 'home', label: 'Genel Bakış', icon: 'dashboard' },
      { key: 'evaluations', label: 'Değerlendirmeler', icon: 'fact_check' },
      { key: 'assigned', label: 'Atanan Çağrılar', icon: 'assignment', badge: openAssigned || undefined },
      { key: 'new', label: 'Yeni Değerlendirme', icon: 'playlist_add_check' },
      { key: 'report', label: 'Raporlar', icon: 'insights' },
      { key: 'objections', label: 'İtirazlar', icon: 'gavel', badge: openObjections || undefined },
      { key: 'weekly', label: 'Haftalık Raporlar', icon: 'speaker_notes' },
    ];
  }
  if (user.role === 'qms') {
    return [
      { key: 'home', label: 'Değerlendirmeler', icon: 'fact_check' },
      // The daily auto-assigned calls (owner 2026-10-02, Task Board #163). Badge = today's
      // still-open ones.
      { key: 'assigned', label: 'Atanan Çağrılar', icon: 'assignment', badge: openAssigned || undefined },
      { key: 'new', label: 'Yeni Değerlendirme', icon: 'playlist_add_check' },
      { key: 'objections', label: 'İtirazlar', icon: 'gavel', badge: openObjections || undefined },
      // Weekly per-agent reports they OWE (owner 2026-09-29, Task Board #145/#146).
      { key: 'weekly', label: 'Haftalık Raporlar', icon: 'speaker_notes' },
    ];
  }
  // Genel Bakış is the TEAM report; "Raporlar" is back (owner 2026-09-09) as the per-agent
  // one — the old brand-rollup version was the duplicate, the Temsilci Raporu is not. Admin
  // and supervisor (brand-scoped, "everything in their brand" — owner 2026-09-18) get every
  // screen, Kullanıcılar included.
  return [
    { key: 'home', label: 'Genel Bakış', icon: 'dashboard' },
    { key: 'evaluations', label: 'Değerlendirmeler', icon: 'fact_check' },
    { key: 'new', label: 'Yeni Değerlendirme', icon: 'playlist_add_check' },
    // Every specialist's daily auto-assigned calls (Task Board #163).
    { key: 'assigned', label: 'Atanan Çağrılar', icon: 'assignment' },
    { key: 'report', label: 'Raporlar', icon: 'insights' },
    // The qms weekly reports — a supervisor reads only their report brand's (server-side).
    { key: 'weekly', label: 'Haftalık Raporlar', icon: 'speaker_notes' },
    { key: 'objections', label: 'İtirazlar', icon: 'gavel', badge: openObjections || undefined },
    { key: 'users', label: 'Kullanıcılar', icon: 'groups' },
  ];
}

/** May this role open the report screens? Mirrors engine/access.js canSeeReports — the
 *  server is the gate, this only keeps a typed URL from rendering a screen whose every
 *  request would 403. */
function isReportReader(user: PanelUser): boolean {
  return user.role === 'admin' || user.role === 'supervisor'
    || (user.role === 'qms' && user.viewAll === true);
}

/** May this role open Kullanıcılar? Mirrors canManageUsers — admin + supervisor, never a qms. */
function isUserManager(user: PanelUser): boolean {
  return user.role === 'admin' || user.role === 'supervisor';
}

/** May this role open the objection list? Mirrors engine/access.js canSeeObjection with no
 *  row: the resolvers, the agent (self-scoped — İtirazlarım) and the qms specialist
 *  (read-only, owner 2026-09-22). Reading it is NOT answering it — that is canResolve. */
function isObjectionReader(user: PanelUser): boolean {
  return isReportReader(user) || user.role === 'agent' || user.role === 'qms';
}

export default function App() {
  const [user, setUser] = useState<PanelUser | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [booting, setBooting] = useState(true);
  const [openObjections, setOpenObjections] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);
  const route = useHashRoute();

  // Global brand filter (topbar). One piece of state for the whole app rather than a
  // per-screen select, so moving between Genel Bakış / Değerlendirmeler / İtirazlar /
  // Raporlar keeps the brand the reader chose — call-agent-admin's brand bar behaves the
  // same way. '' = every brand the caller may see. Persisted so a reload does not silently
  // widen the view back to both brands.
  const [brand, setBrandState] = useState<string>(() => {
    try { return localStorage.getItem('qms:brand') || ''; } catch { return ''; }
  });
  const setBrand = useCallback((b: string) => {
    try { localStorage.setItem('qms:brand', b); } catch { /* private mode */ }
    setBrandState(b);
  }, []);

  // Language: TR source, EN applied as a post-render DOM overlay (see i18n.ts). State lives
  // here, ABOVE the keyed wrapper, so remounting the tree on a language switch never resets it.
  const appRef = useRef<HTMLDivElement>(null);
  const [lang, setLangState] = useState<Lang>(() => {
    try { return localStorage.getItem('qms:lang') === 'en' ? 'en' : 'tr'; } catch { return 'tr'; }
  });
  const setLang = useCallback((l: Lang) => {
    try { localStorage.setItem('qms:lang', l); } catch { /* private mode */ }
    setLangState(l);
  }, []);
  useTranslateDOM(lang, appRef);

  // Session bootstrap. A 401 here is the normal "not signed in" path, not an error.
  useEffect(() => {
    api.me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener('qms:unauthorized', onUnauthorized);
    return () => window.removeEventListener('qms:unauthorized', onUnauthorized);
  }, []);

  // Meta (criteria, rules, brands) is fetched once per session and shared by every page,
  // so the review form and the reports always use the SAME weights.
  useEffect(() => {
    if (!user) { setMeta(null); return; }
    api.getMeta().then(setMeta).catch(() => setMeta(null));
  }, [user]);

  const refreshObjections = useCallback(() => {
    // Only the roles the list route admits (supervisor/admin, and the agent for their own
    // appeals) — a qms would 403.
    if (!user || !isObjectionReader(user)) return;
    api.listObjections('open', brand)
      .then((r) => setOpenObjections(r.openCount))
      .catch(() => { /* a badge is not worth an error screen */ });
  }, [user, brand]);

  useEffect(() => { refreshObjections(); }, [refreshObjections, route.name]);

  // A qms's own open assigned calls for today — the nav badge. Re-read on every navigation so
  // an evaluation saved from the form clears it on the way back.
  const [openAssigned, setOpenAssigned] = useState(0);
  useEffect(() => {
    if (!user || user.role !== 'qms') return;
    api.listAssignments()
      .then((r) => setOpenAssigned(r.counts.mineOpen ?? r.counts.open))
      .catch(() => { /* a badge is not worth an error screen */ });
  }, [user, route.name]);

  // A hash change is not a page load, so the browser keeps the old scroll offset — a
  // detail page opened from the bottom of a long list would start half-way down.
  useEffect(() => { window.scrollTo(0, 0); }, [route.name, route.param]);

  const onLogout = useCallback(async () => {
    try { await api.logout(); } finally { setUser(null); navigate(''); }
  }, []);

  const navItems = useMemo(
    () => (user ? navItemsFor(user, openObjections, openAssigned) : []),
    [user, openObjections, openAssigned],
  );

  // key: remount the tree on a language switch → DOM returns to fresh TR, then useTranslateDOM
  // (if EN) re-applies. display:contents so the wrapper adds no layout box of its own.
  const wrap = (c: JSX.Element) => (
    <div key={`lang-${lang}`} ref={appRef} style={{ display: 'contents' }}>{c}</div>
  );

  if (booting) return wrap(<div className="empty" style={{ paddingTop: 120 }}>Yükleniyor…</div>);
  if (!user) return wrap(<Login onLoggedIn={setUser} lang={lang} onSetLang={setLang} />);

  // A stored brand the user may no longer see (their role changed, or a locked account)
  // must not silently 403 every screen — drop back to "every brand I can see".
  const brandOptions = meta?.brands ?? [];
  const activeBrand = brandOptions.some((b) => b.key === brand) ? brand : '';
  const page = renderPage(route, user, meta, refreshObjections, activeBrand);
  const navRoot = navRootFor(route.name);
  // A qms has no "evaluations" entry — the list is their home, so light that one instead.
  const activeNav = navItems.some((i) => i.key === route.name) ? route.name
    : (navItems.some((i) => i.key === navRoot) ? navRoot : (navRoot === 'evaluations' ? 'home' : navRoot));

  return wrap(
    <div className="app">
      <aside className="rail">
        <div className="rail-logo">
          <span className="logo-tile"><Icon name="graphic_eq" /></span>
          <div style={{ minWidth: 0 }}>
            <div className="rail-name">Ses Kalite</div>
            <div className="rail-kicker">{ROLE_KICKER[user.role] || user.roleLabel}</div>
          </div>
        </div>
        <nav className="rail-nav">
          {navItems.map((item) => {
            const on = activeNav === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`nav${on ? ' on' : ''}`}
                onClick={() => navigate(item.key === 'home' ? '' : item.key)}
              >
                <Icon name={item.icon} />
                <span className="nav-lbl">{item.label}</span>
                {item.badge ? <span className="nav-badge">{item.badge}</span> : on ? <span className="nav-dot" /> : null}
              </button>
            );
          })}
        </nav>
        <div className="rail-foot">
          <span className="pulse" />
          <span>Sistem aktif</span>
          <span className="ver" translate="no">{APP_VERSION}</span>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <BrandFilter brands={brandOptions} value={activeBrand} onSet={setBrand} />
          <div className="topbar-right">
            <div className="topbar-date">
              <Icon name="calendar_today" />
              <span>{longDate(new Date().toISOString())}</span>
            </div>
            <div className="vsep" />
            <LangSwitch lang={lang} onSet={setLang} />
            <div className="vsep" />
            <div className="topbar-user">
              <Avatar name={user.displayName} size="lg" />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <span className="nm">{user.displayName}</span>
                <span className="rl">{user.roleLabel}{user.brandKey ? ` · ${user.brandKey}` : ''}</span>
              </span>
            </div>
            <button className="iconbtn" type="button" onClick={() => setPwOpen(true)} title="Şifre değiştir">
              <Icon name="key" />
            </button>
            <button className="iconbtn" type="button" onClick={onLogout} title="Çıkış">
              <Icon name="logout" />
            </button>
          </div>
        </header>
        <main className="content">
          {/* The weekly-report reminder (owner 2026-09-29): a qms sees it everywhere but on the
              reports screen itself while any report of the due week is missing. */}
          {user.role === 'qms' && route.name !== 'weekly'
            ? <WeeklyReportBanner routeKey={`${route.name}/${route.param ?? ''}`} />
            : null}
          {page}
        </main>
      </div>
      {pwOpen ? <ChangePassword onClose={() => setPwOpen(false)} /> : null}
    </div>
  );
}

/** `#/new/<id>` → the assignment id, or null for a plain `#/new`. */
function assignmentIdOf(param: string | undefined): number | null {
  const n = Number(param);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Which sidebar entry should light up for a detail route. */
function navRootFor(name: string): string {
  if (name === 'evaluation') return 'evaluations';
  if (name === 'agent') return 'report';
  return name;
}

function renderPage(
  route: Route,
  user: PanelUser,
  meta: Meta | null,
  refreshObjections: () => void,
  brand: string,
): JSX.Element {
  const notFound = <div className="empty">Sayfa bulunamadı.</div>;
  switch (route.name) {
    case 'home':
      // A qms has no Genel Bakış (it is a report) — their home is the evaluations list.
      if (user.role === 'agent') return <MyDashboard user={user} />;
      if (user.role === 'qms' && user.viewAll !== true) return <Evaluations user={user} meta={meta} brand={brand} />;
      return <QmsDashboard user={user} meta={meta} brand={brand} />;
    case 'evaluations':
      return <Evaluations user={user} meta={meta} brand={brand} />;
    case 'evaluation':
      return <EvalDetail id={Number(route.param)} user={user} meta={meta} onChanged={refreshObjections} />;
    case 'new':
      // Every non-agent may create (supervisors too). Rendering the form to an agent — whose
      // POST the server refuses — would only lie; the server gate is the real one.
      // #/new/<assignmentId> opens the form pre-filled from an auto-assigned call (Task Board
      // #163); keyed so moving between two assignments remounts a clean form.
      if (user.role === 'agent') return notFound;
      return <NewEvaluation key={`new-${assignmentIdOf(route.param) ?? ''}`} meta={meta} assignmentId={assignmentIdOf(route.param)} />;
    case 'assigned':
      return user.role !== 'agent' ? <Assignments user={user} meta={meta} brand={brand} /> : notFound;
    case 'objections':
      // supervisor/admin (the queue) and the agent (İtirazlarım — their own appeals, owner
      // 2026-09-21). The list route 403s a qms (owner 2026-09-18), so for them the screen
      // would render as an error, not as a list.
      return isObjectionReader(user)
        ? <Objections user={user} meta={meta} brand={brand} onChanged={refreshObjections} />
        : notFound;
    case 'report':
      // Agents keep their own period report ("Gelişimim"); supervisor/admin get "Raporlar",
      // whose subject is one AGENT at a time (Temsilci Raporu) — not the team rollup that
      // Genel Bakış already is. A qms has neither (server: /api/reports/* 403).
      if (user.role === 'agent') return <AgentReport agentId={null} user={user} meta={meta} />;
      return isReportReader(user) ? <Reports user={user} meta={meta} brand={brand} /> : notFound;
    case 'agent': {
      if (!isReportReader(user) && user.role !== 'agent') return notFound;
      // A non-numeric param (#/agent, #/agent/foo) must not send NaN to /api/reports/agent/NaN
      // (?? does not catch NaN) — normalize to null so AgentReport falls back to 'me'.
      const pid = Number(route.param);
      return <AgentReport agentId={Number.isInteger(pid) ? pid : null} user={user} meta={meta} />;
    }
    case 'weekly':
      // qms writes, supervisor/admin read (the text only for their report brand — server).
      return user.role !== 'agent' ? <WeeklyReports user={user} brand={brand} /> : notFound;
    case 'users':
      // admin + supervisor reach this (a supervisor's screen is brand-scoped, server-side).
      // Hiding a menu item is not authorization — the API re-checks every /api/users call —
      // but rendering the create-user form to a QMS specialist who types the URL only tells
      // them it was forbidden after they fill it in.
      return isUserManager(user) ? <Users user={user} meta={meta} /> : notFound;
    default:
      return notFound;
  }
}
